import { TFunction } from 'language/translator'
import type {
  TMultiOptionDto,
  TMultiOptionsConfigDto,
  TSingleConfigDto
} from '@homepal/api-contract'

// Past this many the excluded options are counted instead of named.
const MAX_NAMED_EXCLUDED = 2

// the object row type of the rows without one
const NO_OBJECT_ROW_TYPE = ''

/**
 * The label of an option of a MULTI_OPTIONS config, the missing value for the
 * object rows without a type.
 */
export const getMultiOptionLabel = (option: TMultiOptionDto, t: TFunction) => {
  return option.value === NO_OBJECT_ROW_TYPE
    ? t('global', 'missing_value')
    : option.label
}

export const getGeneralOptions = (general: TSingleConfigDto, t: TFunction) => {
  return general.options.map((option) => ({
    title: option.value === null ? t('global', 'none') : option.label,
    id: option.value ?? 'none'
  }))
}

/**
 * The value of a MULTI_OPTIONS config as a line: the options it leaves out,
 * named while there are few.
 */
export const getExcludedSummary = (
  general: TMultiOptionsConfigDto,
  t: TFunction
) => {
  const excluded = general.options
    .filter((option) => general.value.includes(option.value))
    .map((option) => getMultiOptionLabel(option, t))

  if (excluded.length === 0) {
    return t('configuration', 'all_included')
  }

  if (excluded.length <= MAX_NAMED_EXCLUDED) {
    return t('configuration', 'excluded_types', false, {
      types: excluded.join(', ')
    })
  }

  return t('configuration', 'excluded_of', false, {
    excluded: String(excluded.length),
    total: String(general.options.length)
  })
}

/**
 * The number of options of a MULTI_OPTIONS config that are new since the
 * customer last saved, as a tag label, or null when none is.
 */
export const getNewSummary = (
  general: TMultiOptionsConfigDto,
  t: TFunction
) => {
  const newCount = general.options.filter((option) => option.new).length

  if (newCount === 0) {
    return null
  }

  return t(
    'configuration',
    newCount === 1 ? 'new_summary_one' : 'new_summary',
    false,
    { count: String(newCount) }
  )
}

/**
 * Whether a config's value has changed after the latest data run, which
 * then has yet to read it.
 */
export const isPendingDataRun = (
  general: TMultiOptionsConfigDto,
  latestJobRun: string | null | undefined
) => {
  if (!general.changed_at) {
    return false
  }

  return (
    !latestJobRun ||
    new Date(general.changed_at).getTime() > new Date(latestJobRun).getTime()
  )
}
